import { Component, OnInit, TemplateRef } from '@angular/core';
import {
  ApprovalStatus,
  ApproveExportAudienceInput,
  ApproveExportAudienceMutation,
  ApproveExportAudienceMutationVariables,
  Engagements,
  ExportStatus,
  ExportAudience,
  GetExportAudiencesQuery,
  GetExportAudiencesQueryVariables,
  AudienceType,
} from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { AppService } from '../../../shared/services/app.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GetExportAudiences } from '../../../../graphql/queries';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { AT_LEAST_ONE_ALPHABET } from '../../../shared/utils/constants';
import { ApproveExportAudience } from '../../../../graphql/mutations';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AuthService } from '../../../shared/services/auth.service';

@Component({
  selector: 'app-audience-export-history',
  templateUrl: './audience-export-history.component.html',
  styleUrl: './audience-export-history.component.scss',
})
export class AudienceExportHistoryComponent implements OnInit {
  readonly supportedPageSizes: number[] = [10, 25, 50];
  readonly hasAudienceExportHistoryActionEntitlement: string[] = ['app.dcr-portal.audience-export-history.actions'];

  engagement!: Engagements;
  isLoading?: boolean;
  headers: any[] = [];
  rows: ExportAudience[] = [];
  lastModifiedByFilter: { text: string; value: string }[] = [];
  rowsToDisplay: ExportAudience[] = [];
  totalCount?: number;

  visible = false;
  searchValue?: string = '';

  rejectModalRef!: NzModalRef;
  rejectReason: UntypedFormControl = new FormControl('', [
    Validators.required,
    Validators.minLength(5),
    Validators.pattern(AT_LEAST_ONE_ALPHABET),
  ]);

  approveModalRef!: NzModalRef;
  dataGovernanceApproval: UntypedFormControl = new FormControl(null, [Validators.required]);

  constructor(
    private apiService: ApiService,
    public appService: AppService,
    private nzMessageService: NzMessageService,
    private nzModalService: NzModalService,
    private userManagementService: UserManagementService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.appService.activeEngagement$.subscribe((engagement: Engagements) => {
      this.engagement = engagement;
      this.getExportAudienceHistory();
    });
  }

  setHeaders(): void {
    this.headers = [
      {
        name: 'Export ID',
        sortOrder: null,
        sortFn: (a: ExportAudience, b: ExportAudience) => a.exportId?.localeCompare(b.exportId ?? ''),
        sortDirections: ['ascend', 'descend', null],
        left: true,
      },
      {
        name: 'Query/Lookalike ID',
        width: '10rem',
      },
      {
        name: 'Query/Lookalike Name',
        width: '15rem',
      },
      {
        name: 'Target Channel',
        width: '8rem',
      },
      {
        name: 'Audience From',
        sortOrder: null,
        sortFn: (a: ExportAudience, b: ExportAudience) => a.audienceFrom?.localeCompare(b.audienceFrom ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: AudienceType.QUERY, value: AudienceType.QUERY },
          { text: AudienceType.LOOKALIKE_MODEL, value: AudienceType.LOOKALIKE_MODEL },
        ],
        filterFn: (type: string, row: ExportAudience) => {
          if (!row.audienceFrom) row.audienceFrom = AudienceType.QUERY;
          return row.audienceFrom?.indexOf(type) !== -1;
        },
        width: '10rem',
      },
      {
        name: 'Exported By',
        key: 'exportedBy',
        listOfFilter: this.lastModifiedByFilter,
        filterFn: (exportedByList: string[], row: ExportAudience) =>
          exportedByList.some((exportedBy) => row.exportedBy && row.exportedBy.indexOf(exportedBy) !== -1),
        filterMultiple: true,
        width: '13rem',
      },
      {
        name: 'Exported At',
        sortOrder: 'descend',
        sortFn: (a: ExportAudience, b: ExportAudience) =>
          new Date(a.exportedAt ?? '').getTime() - new Date(b.exportedAt ?? '').getTime(),
        sortDirections: ['ascend', 'descend', null],
        width: '14rem',
      },
      {
        name: 'Expire At',
        sortOrder: 'descend',
        sortFn: (a: ExportAudience, b: ExportAudience) =>
          new Date(a.expireAt ?? '').getTime() - new Date(b.expireAt ?? '').getTime(),
        sortDirections: ['ascend', 'descend', null],
        width: '14rem',
      },
      {
        name: 'Status',
        sortOrder: null,
        sortFn: (a: ExportAudience, b: ExportAudience) => a.approvalStatus?.localeCompare(b.approvalStatus ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: ApprovalStatus.APPROVED, value: ApprovalStatus.APPROVED },
          { text: ApprovalStatus.REJECTED, value: ApprovalStatus.REJECTED },
          { text: ApprovalStatus.PENDING, value: ApprovalStatus.PENDING },
        ],
        filterFn: (type: string, row: ExportAudience) => row.approvalStatus?.indexOf(type) !== -1,
        width: '6rem',
      },
      {
        name: 'Export Status',
        sortOrder: null,
        sortFn: (a: ExportAudience, b: ExportAudience) => a.exportStatus?.localeCompare(b.exportStatus ?? ''),
        sortDirections: ['ascend', 'descend', null],
        listOfFilter: [
          { text: ExportStatus.SUBMITTED, value: ExportStatus.SUBMITTED },
          { text: ExportStatus.SUBMISSION_FAILED, value: ExportStatus.SUBMISSION_FAILED },
          { text: ExportStatus.IN_PROGRESS, value: ExportStatus.IN_PROGRESS },
          { text: ExportStatus.SUCCESS, value: ExportStatus.SUCCESS },
          { text: ExportStatus.FAILED, value: ExportStatus.FAILED },
        ],
        filterFn: (type: string, row: ExportAudience) => row.exportStatus === type,
        width: '10rem',
      },
      {
        name: 'Action',
        width: '5rem',
        right: true,
      },
    ];
  }

  setLastModifiedByFilter(): any {
    this.headers.find((head) => head.key === 'lastModifiedBy').listOfFilter = this.rows
      .map((record: any) => record.lastModifiedBy)
      .filter((lastModifiedBy, index, arr) => arr.indexOf(lastModifiedBy) === index)
      .map((lastModifiedBy) => {
        return {
          text: lastModifiedBy,
          value: lastModifiedBy,
        };
      });
  }

  getPageData(): void {
    this.totalCount = this.rowsToDisplay?.length;
  }

  reset(): void {
    this.searchValue = '';
    this.search();
  }

  search(): void {
    this.visible = false;
    this.rowsToDisplay = this.rows?.filter(
      (item: any) => item.name?.toLowerCase().indexOf(this.searchValue?.toLowerCase()) !== -1,
    );
  }

  getExportAudienceHistory(): void {
    this.isLoading = true;
    this.apiService
      .query<GetExportAudiencesQuery, GetExportAudiencesQueryVariables>(GetExportAudiences, {
        engagementId: this.engagement.engagement,
      })
      .then((response) => {
        const results = response.data?.GetExportAudiences;
        this.rowsToDisplay = this.rows = results as ExportAudience[];

        this.setHeaders();
        this.totalCount = this.rows.length;
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to fetch list due to error ${error}`);
      })
      .finally(() => (this.isLoading = false));
  }

  getApprovalStatusColor(status?: ApprovalStatus | null): string {
    switch (status) {
      case ApprovalStatus.APPROVED:
        return 'success';
      case ApprovalStatus.PENDING:
        return 'warning';
      case ApprovalStatus.REJECTED:
        return 'error';
      default:
        return '';
    }
  }

  getExportStatusColor(status?: ExportStatus | null): string {
    switch (status) {
      case ExportStatus.SUCCESS:
        return 'success';
      case ExportStatus.SUBMITTED:
        return 'primary';
      case ExportStatus.SUBMISSION_FAILED:
        return 'error';
      case ExportStatus.IN_PROGRESS:
        return 'warning';
      case ExportStatus.FAILED:
        return 'error';
      default:
        return '';
    }
  }

  rejectExport(data: ExportAudience, rejectModalTemplateRef: TemplateRef<void>): void {
    this.rejectReason.reset();
    this.rejectModalRef = this.nzModalService.create({
      nzTitle: 'Reject Export',
      nzContent: rejectModalTemplateRef,
      nzOnOk: () => this.handleReject(data),
      nzFooter: null,
    });
  }

  approveExport(data: ExportAudience, approveModalTemplateRef: TemplateRef<void>): void {
    this.dataGovernanceApproval.reset();
    this.approveModalRef = this.nzModalService.create({
      nzTitle: 'Approve Export',
      nzContent: approveModalTemplateRef,
      nzOnOk: () => this.handleApprove(data),
      nzFooter: null,
    });
  }

  allowActions(row: ExportAudience): boolean {
    if (!this.appService.isEngagementActive) return false;
    const hasAudienceExportHistoryActionEntitlement = this.authService.hasEntitlements(
      this.hasAudienceExportHistoryActionEntitlement,
    );
    const loggedInOrgType = this.userManagementService.getOrgType();
    return (
      row.approvalStatus === ApprovalStatus.PENDING &&
      hasAudienceExportHistoryActionEntitlement &&
      row.orgType !== loggedInOrgType
    );
  }

  handleApprove(data: ExportAudience): void {
    this.approveOrRejectExportAudienceAPI(data, ApprovalStatus.APPROVED);
  }

  handleReject(data: ExportAudience): void {
    this.approveOrRejectExportAudienceAPI(data, ApprovalStatus.REJECTED);
  }

  approveOrRejectExportAudienceAPI(data: ExportAudience, status: ApprovalStatus): void {
    this.isLoading = true;
    const { engagement, exportId } = data;
    const payload: ApproveExportAudienceInput = {
      engagement,
      exportId,
      approvalStatus: status,
      approvedBy: this.userManagementService.getEmail(),
    };
    if (status === ApprovalStatus.REJECTED) {
      payload.rejectReason = this.rejectReason.value;
    }
    this.apiService
      .query<ApproveExportAudienceMutation, ApproveExportAudienceMutationVariables>(ApproveExportAudience, {
        updateAudienceExportRequest: payload,
      })
      .then((response) => {
        const result = response.data?.ApproveExportAudience;
        if (status === ApprovalStatus.APPROVED) {
          this.nzMessageService.success(`Export Initiated for export ID ${result?.exportId}`);
        } else if (status === ApprovalStatus.REJECTED) {
          this.nzMessageService.success(`Export Rejected for export ID ${result?.exportId}`);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed with error ${error.message}`);
      })
      .finally(() => {
        this.isLoading = false;
        this.getExportAudienceHistory();
      });
  }

  getAudienceFrom(audienceFrom: string): string {
    if (AudienceType.LOOKALIKE_MODEL === audienceFrom) return 'Lookalike model';
    return 'Query result';
  }
}
